import {
  AccountCircle,
  AccountTree,
  AccessTime,
  Badge,
  Category,
  CheckCircle,
  CloudDone,
  ErrorOutline,
  FactCheck,
  HelpOutline,
  LinkOff,
  MenuBook,
  Payment,
  Person,
  PhotoCamera,
  Refresh,
  Search,
  Send,
  Settings,
  ShoppingBag,
  SmartToy,
  Verified,
  WhatsApp,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  FormControl,
} from "@mui/material";
import React from "react";
import { FeedbackSnackbar } from "../components/design/FeedbackSnackbar";
import { MenuImageUploader } from "../components/elements/MenuImageUploader";
import {
  dashboardCardSx,
  dashboardPagePaddingSx,
  dashboardPageSx,
  dashboardSectionTitleSx,
} from "../components/layout/dashboardStyles";
import { PageLoadingState } from "../components/layout/PageLoadingState";
import { MAX_IMAGES } from "../config/config";
import { AISettings, WSSettings } from "../data/models/Config/ConfigModel";
import { metaOnboardingManager } from "../data/managers/MetaOnboardingManager";
import {
  MetaWhatsappBusinessProfile,
  MetaOnboardingConfigResponse,
  MetaWhatsappTemplateComponent,
  MetaWhatsappTemplate,
  MetaWhatsappTemplateCreateRequest,
  MetaWhatsappConnection,
} from "../data/models/Meta/MetaOnboardingModels";
import { useStorageImages } from "../data/providers/StorageProvider";
import { toTitleCase } from "../utils/StringTool";
import { useSettings } from "./hooks/useSettings";
import { getConfiguredApiBaseUrl } from "../utils/RequestBuilder";
import { WORKFLOW_LABELS } from "../config/companyCapabilities";
import { IntegrationCodeEnum } from "../config/access";
import { LOANS_TEMPLATE_DRAFTS } from "../config/loansTemplates";
import { MAPOS_ORDER_TEMPLATE_DRAFTS } from "../config/maposOrderTemplates";
import { COEXISTENCE_NOTIFICATION_TEMPLATE_DRAFTS } from "../config/coexistenceNotificationTemplates";
import { BanchatConnectionSettings } from "../components/settings/BanchatConnectionSettings";

declare global {
  interface Window {
    FB?: any;
    fbAsyncInit?: () => void;
  }
}

type SettingsFieldConfig<T> = {
  key: keyof T;
  label: string;
  multiline?: boolean;
  rows?: number;
  sensitive?: boolean;
  lockWhenHasValue?: boolean;
  helperText?: string;
};

type NormalizedSchedule =
  | {
      mode: "fixed";
      fixed: {
        opening: string;
        closing: string;
      };
    }
  | {
      mode: "variable";
      days: Record<string, { opening: string; closing: string } | null>;
    };

const SENSITIVE_MASK = "************";
const LOCKED_FIELD_HELPER_TEXT = "Campo bloqueado porque ya tiene un valor guardado.";
const FACEBOOK_SDK_SCRIPT_ID = "facebook-jssdk";
const DEFAULT_TEST_TEMPLATE_NAME = "lypro_saludo";
const DEFAULT_TEST_TEMPLATE_LANGUAGE = "es";
const META_LOGIN_TIMEOUT_MS = 120000;
const META_SIGNUP_FINISH_EVENTS = new Set([
  "FINISH",
  "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
]);
const TRUSTED_FACEBOOK_ORIGINS = new Set([
  "https://www.facebook.com",
  "https://web.facebook.com",
]);

const DEFAULT_FIXED_SCHEDULE = {
  opening: "07:00",
  closing: "17:00",
};

const DEFAULT_VARIABLE_DAYS = {
  lunes: null,
  martes: null,
  miercoles: null,
  jueves: null,
  viernes: null,
  sabado: null,
  domingo: null,
};

const AI_PROFILE_FIELDS: SettingsFieldConfig<AISettings>[] = [
  { key: "name", label: "Nombre del negocio" },
  { key: "nameBot", label: "Nombre del bot" },
  { key: "companyId", label: "ID de compañía Lypro", lockWhenHasValue: true },
  { key: "idMapos", label: "ID Mapos", lockWhenHasValue: true },
  {
    key: "description",
    label: "Descripción",
    multiline: true,
    rows: 6,
  },
  {
    key: "recommendations",
    label: "Recomendaciones",
    multiline: true,
    rows: 5,
  },
  {
    key: "synonyms",
    label: "Sinonimos",
    multiline: true,
    rows: 5,
  },
  {
    key: "whatsappDebounceSeconds",
    label: "Espera de respuesta de WhatsApp (segundos)",
    helperText:
      "Usa 7 para el valor recomendado. Usa 0 para responder sin espera; máximo 60 segundos.",
  },
];

const WS_SETTINGS_FIELDS: SettingsFieldConfig<WSSettings>[] = [
  {
    key: "accessToken",
    label: "Access Token (Cloud API)",
    sensitive: true,
    lockWhenHasValue: true,
    helperText:
      "Token privado generado por Meta durante el registro insertado. Se muestra protegido.",
  },
  {
    key: "phoneNumberId",
    label: "Phone Number ID (Cloud API)",
    sensitive: true,
    lockWhenHasValue: true,
    helperText: "ID del número de WhatsApp Business conectado con Meta.",
  },
  {
    key: "whatsappId",
    label: "WhatsApp Business Account ID (WABA ID)",
    sensitive: true,
    lockWhenHasValue: true,
    helperText: "ID de la cuenta de WhatsApp Business autorizada por el negocio.",
  },
  {
    key: "metaWebhookProxyUrl",
    label: "Meta Webhook Target URL",
    helperText:
      "Opcional. Si lo dejas vacío, LyPro usa el webhook de Meta configurado para WhatsApp Cloud API.",
  },
  {
    key: "maposBotWebhookUrl",
    label: "URL del bot externo",
    helperText:
      "URL HTTPS del endpoint servidor a servidor del bot externo. Debe terminar en /integrations/lypro/whatsapp.",
  },
  {
    key: "maposBotWebhookSecret",
    label: "Secreto compartido del bot externo",
    sensitive: true,
    helperText:
      "Se guarda cifrado y debe coincidir con el secreto configurado en el bot externo. Déjalo vacío para conservarlo o escribe uno nuevo para reemplazarlo.",
  },
];

const normalizeSchedule = (
  schedule?: AISettings["schedule"],
): NormalizedSchedule => {
  if (schedule?.mode === "variable") {
    return {
      mode: "variable",
      days: {
        ...DEFAULT_VARIABLE_DAYS,
        ...(schedule.days ?? {}),
      },
    };
  }

  return {
    mode: "fixed",
    fixed: {
      opening: schedule?.fixed?.opening ?? DEFAULT_FIXED_SCHEDULE.opening,
      closing: schedule?.fixed?.closing ?? DEFAULT_FIXED_SCHEDULE.closing,
    },
  };
};

type EmbeddedSignupPayload = {
  wabaId?: string;
  phoneNumberId?: string;
  businessId?: string;
  rawSignupPayload?: Record<string, any>;
};

type EmbeddedSignupMessage = {
  type?: string;
  event?: string;
  data?: Record<string, any>;
};

type CloudApiStepStatus = "done" | "current" | "pending" | "error";
type WhatsAppOnboardingMode = "standard" | "coexistence";

const META_STATUS_LABELS: Record<string, string> = {
  NOT_CONNECTED: "Sin conectar",
  CONNECTED: "Conectado",
  ERROR: "Requiere atención",
  PENDING: "En progreso",
  CONSULTANDO: "Consultando",
};

const META_QUALITY_LABELS: Record<string, string> = {
  GREEN: "Alta",
  YELLOW: "Media",
  RED: "Baja",
  UNKNOWN: "Sin datos",
};

const META_TEMPLATE_STATUS_LABELS: Record<string, string> = {
  APPROVED: "Aprobada",
  PENDING: "En revisión",
  REJECTED: "Rechazada",
  PAUSED: "Pausada",
  DISABLED: "Deshabilitada",
};

const META_PAYMENT_SETTINGS_URL = "https://business.facebook.com/settings/payment-methods";

const WHATSAPP_VERTICAL_OPTIONS = [
  { value: "", label: "Sin categoría" },
  { value: "RESTAURANT", label: "Restaurante" },
  { value: "FOOD_AND_GROCERY", label: "Alimentos y mercado" },
  { value: "RETAIL", label: "Retail" },
  { value: "SERVICES", label: "Servicios" },
  { value: "EDUCATION", label: "Educacion" },
  { value: "HEALTH", label: "Salud" },
  { value: "TRAVEL", label: "Viajes" },
  { value: "OTHER", label: "Otro" },
];

const emptyBusinessProfile: MetaWhatsappBusinessProfile = {
  about: "",
  address: "",
  description: "",
  email: "",
  websites: [],
  vertical: "",
  profilePictureHandle: "",
};

const emptyTemplateDraft: MetaWhatsappTemplateCreateRequest = {
  name: "",
  language: "es",
  category: "UTILITY",
  headerText: "",
  bodyText: "",
  footerText: "",
  buttons: [],
};

const TEMPLATE_CATEGORY_OPTIONS = [
  { value: "UTILITY", label: "Utilidad", description: "Confirmaciones, avisos y actualizaciones de pedidos." },
  { value: "MARKETING", label: "Marketing", description: "Promociones, novedades y campañas comerciales." },
  { value: "AUTHENTICATION", label: "Autenticación", description: "Códigos y mensajes de verificación." },
] as const;

const TEMPLATE_LANGUAGE_OPTIONS = [
  { value: "es", label: "Español" },
  { value: "en_US", label: "English (US)" },
  { value: "pt_BR", label: "Português (Brasil)" },
] as const;

const getTemplateComponent = (
  template: MetaWhatsappTemplate,
  type: MetaWhatsappTemplateComponent["type"],
) => template.components?.find((component) => component.type === type);

const TemplatePreview = ({
  headerText,
  bodyText,
  footerText,
  buttons,
}: {
  headerText?: string;
  bodyText?: string;
  footerText?: string;
  buttons?: string[];
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.35,
      width: "100%",
      maxWidth: 360,
      bgcolor: "#e8f5e9",
      color: "#18251b",
      borderRadius: "5px 14px 14px 14px",
      border: "1px solid rgba(46, 125, 50, 0.18)",
    }}
  >
    {headerText?.trim() && (
      <Typography variant="body2" sx={{ fontWeight: 900, mb: 0.65, color: "#132016" }}>
        {headerText}
      </Typography>
    )}
    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.45 }}>
      {bodyText?.trim() || "Aquí verás una vista previa de tu mensaje."}
    </Typography>
    {footerText?.trim() && (
      <Typography variant="caption" sx={{ display: "block", mt: 0.9, color: "#55705a" }}>
        {footerText}
      </Typography>
    )}
    {!!buttons?.filter(Boolean).length && (
      <Stack spacing={0.6} sx={{ mt: 1 }}>
        {buttons.filter(Boolean).slice(0, 3).map((button) => (
          <Box
            key={button}
            sx={{
              py: 0.55,
              px: 1,
              textAlign: "center",
              color: "#1677c8",
              bgcolor: "rgba(255,255,255,0.8)",
              borderRadius: 1,
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {button}
          </Box>
        ))}
      </Stack>
    )}
    <Typography variant="caption" sx={{ display: "block", mt: 0.75, textAlign: "right", color: "#718176", fontSize: 10 }}>
      Ahora ✓✓
    </Typography>
  </Paper>
);

const parseMaybeJson = (value: any) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const readEmbeddedSignupPayload = (value: any): EmbeddedSignupPayload => {
  const data = parseMaybeJson(value);
  const payload = data?.data || data;
  return {
    wabaId:
      payload?.waba_id ||
      payload?.wabaId ||
      payload?.whatsapp_business_account_id,
    phoneNumberId:
      payload?.phone_number_id ||
      payload?.phoneNumberId ||
      payload?.phone_number,
    businessId: payload?.business_id || payload?.businessId,
    rawSignupPayload:
      data && typeof data === "object" ? (data as Record<string, any>) : undefined,
  };
};

const isTrustedFacebookOrigin = (origin: string) =>
  TRUSTED_FACEBOOK_ORIGINS.has(origin);

const isEmbeddedSignupFinish = (data: EmbeddedSignupMessage) =>
  data?.type === "WA_EMBEDDED_SIGNUP" &&
  Boolean(data.event && META_SIGNUP_FINISH_EVENTS.has(data.event));

const loadFacebookSdk = (
  config: MetaOnboardingConfigResponse,
): Promise<void> => {
  if (window.FB) {
    window.FB.init({
      appId: config.appId,
      cookie: true,
      xfbml: true,
      version: config.graphVersion,
    });
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    window.fbAsyncInit = () => {
      window.FB?.init({
        appId: config.appId,
        cookie: true,
        xfbml: true,
        version: config.graphVersion,
      });
      resolve();
    };

    if (document.getElementById(FACEBOOK_SDK_SCRIPT_ID)) {
      const waitForSdk = window.setInterval(() => {
        if (!window.FB) return;
        window.clearInterval(waitForSdk);
        window.FB.init({
          appId: config.appId,
          cookie: true,
          xfbml: true,
          version: config.graphVersion,
        });
        resolve();
      }, 100);
      return;
    }
    const script = document.createElement("script");
    script.id = FACEBOOK_SDK_SCRIPT_ID;
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error("No se pudo cargar Facebook SDK"));
    document.body.appendChild(script);
  });
};

type SettingsPageProps = {
  cloudApiOnly?: boolean;
};

const SettingsPage: React.FC<SettingsPageProps> = ({ cloudApiOnly = false }) => {
  const {
    companyId,
    tabIndex,
    changeTab,
    aiSettings,
    lastAISettings,
    dialogAISettingsOpen,
    setDialogAISettingsOpen,
    loadingAISettings,
    openAISettingsDialog,
    confirmUpdateAiSettings,
    aiSettingsFieldChange,
    hasAISettingsChanges,
    wsApiSettings,
    lastWsApiSettings,
    dialogWsApiSettingsOpen,
    setDialogWsApiSettingsOpen,
    loadingWsAPISettings,
    openWsAPISettingsDialog,
    confirmUpdateWsAPISettings,
    wsSettingsFieldChange,
    hasWsSettingsChanges,
    lastAllConfig,
    dialogSyncSettingsOpen,
    setDialogSyncSettingsOpen,
    loadingSyncSettings,
    openSyncDialog,
    confirmSyncSettings,
    images,
    loadingImages: loading,
    errorImages: error,
    progress,
    uploadImages: handleAddImages,
    deleteImage: handleRemoveImage,
    snackbarOpen,
    setSnackbarOpen,
    snackbarMessage,
    setSnackbarMessage,
    snackbarSeverity,
    setSnackbarSeverity,
    refreshAllData,
  } = useSettings();

  const [sensitiveEditing, setSensitiveEditing] = React.useState<
    Record<string, boolean>
  >({});
  const [sensitiveBackups, setSensitiveBackups] = React.useState<
    Record<string, string>
  >({});
  const [metaWhatsappStatus, setMetaWhatsappStatus] =
    React.useState<MetaWhatsappConnection>();
  const [loadingMetaStatus, setLoadingMetaStatus] = React.useState(false);
  const [connectingWhatsApp, setConnectingWhatsApp] = React.useState(false);
  const [resettingWhatsApp, setResettingWhatsApp] = React.useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = React.useState("");
  const [testTemplateName, setTestTemplateName] = React.useState(
    DEFAULT_TEST_TEMPLATE_NAME,
  );
  const [testTemplateLanguage, setTestTemplateLanguage] = React.useState(
    DEFAULT_TEST_TEMPLATE_LANGUAGE,
  );
  const [sendingTestMessage, setSendingTestMessage] = React.useState(false);
  const [validatingCloudApi, setValidatingCloudApi] = React.useState(false);
  const [registrationPin, setRegistrationPin] = React.useState("");
  const [registeringPhone, setRegisteringPhone] = React.useState(false);
  const [resetDialogOpen, setResetDialogOpen] = React.useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = React.useState(false);
  const [testTemplateDialogOpen, setTestTemplateDialogOpen] = React.useState(false);
  const [businessProfile, setBusinessProfile] =
    React.useState<MetaWhatsappBusinessProfile>(emptyBusinessProfile);
  const [loadingBusinessProfile, setLoadingBusinessProfile] = React.useState(false);
  const [savingBusinessProfile, setSavingBusinessProfile] = React.useState(false);
  const [uploadingProfileLogo, setUploadingProfileLogo] = React.useState(false);
  const [greetingTemplate, setGreetingTemplate] =
    React.useState<MetaWhatsappTemplate | null>();
  const [loadingGreetingTemplate, setLoadingGreetingTemplate] = React.useState(false);
  const [creatingGreetingTemplate, setCreatingGreetingTemplate] = React.useState(false);
  const [templateCatalog, setTemplateCatalog] = React.useState<MetaWhatsappTemplate[]>(
    [],
  );
  const [loadingTemplateCatalog, setLoadingTemplateCatalog] = React.useState(false);
  const [createTemplateDialogOpen, setCreateTemplateDialogOpen] = React.useState(false);
  const [creatingTemplate, setCreatingTemplate] = React.useState(false);
  const [templateDraft, setTemplateDraft] =
    React.useState<MetaWhatsappTemplateCreateRequest>(emptyTemplateDraft);
  const [templateSearch, setTemplateSearch] = React.useState("");
  const [templateStatusFilter, setTemplateStatusFilter] = React.useState("ALL");
  const [templateCategoryFilter, setTemplateCategoryFilter] = React.useState("ALL");
  const [cloudApiTab, setCloudApiTab] = React.useState<"connect" | "profile" | "templates" | "config">(
    "connect",
  );
  const profileLogoInputRef = React.useRef<HTMLInputElement | null>(null);
  const profileLogoStorage = useStorageImages(companyId, "whatsapp-profile");

  React.useEffect(() => {
    setSensitiveEditing({});
    setSensitiveBackups({});
  }, [lastAISettings, lastWsApiSettings]);

  const refreshMetaWhatsappStatus = React.useCallback(async () => {
    setLoadingMetaStatus(true);
    try {
      const result = await metaOnboardingManager.getStatus();
      setMetaWhatsappStatus(result?.whatsapp);
    } catch (error) {
      setSnackbarMessage("No se pudo consultar el estado de WhatsApp");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoadingMetaStatus(false);
    }
  }, [setSnackbarMessage, setSnackbarOpen, setSnackbarSeverity]);

  React.useEffect(() => {
    refreshMetaWhatsappStatus();
  }, [refreshMetaWhatsappStatus]);

  React.useEffect(() => {
    const persistedWhatsappStatus = lastAllConfig?.metaConnections?.whatsapp;
    if (!persistedWhatsappStatus) return;
    setMetaWhatsappStatus(persistedWhatsappStatus);
  }, [lastAllConfig]);

  const refreshBusinessProfile = React.useCallback(async () => {
    if (!lastWsApiSettings?.phoneNumberId) return;
    setLoadingBusinessProfile(true);
    try {
      const result = await metaOnboardingManager.getBusinessProfile();
      setBusinessProfile({
        ...emptyBusinessProfile,
        ...(result || {}),
        websites: result?.websites || [],
      });
    } catch {
      setBusinessProfile({
        ...emptyBusinessProfile,
        ...(metaWhatsappStatus?.businessProfile || {}),
        websites: metaWhatsappStatus?.businessProfile?.websites || [],
      });
    } finally {
      setLoadingBusinessProfile(false);
    }
  }, [lastWsApiSettings?.phoneNumberId, metaWhatsappStatus?.businessProfile]);

  React.useEffect(() => {
    if (!lastWsApiSettings?.phoneNumberId) return;
    refreshBusinessProfile();
  }, [lastWsApiSettings?.phoneNumberId, refreshBusinessProfile]);

  const refreshGreetingTemplate = React.useCallback(async () => {
    if (!lastWsApiSettings?.whatsappId) return;
    setLoadingGreetingTemplate(true);
    try {
      const result = await metaOnboardingManager.getGreetingTemplate();
      setGreetingTemplate(result || null);
    } catch {
      setGreetingTemplate(metaWhatsappStatus?.greetingTemplate || null);
    } finally {
      setLoadingGreetingTemplate(false);
    }
  }, [lastWsApiSettings?.whatsappId, metaWhatsappStatus?.greetingTemplate]);

  React.useEffect(() => {
    if (!lastWsApiSettings?.whatsappId) return;
    refreshGreetingTemplate();
  }, [lastWsApiSettings?.whatsappId, refreshGreetingTemplate]);

  React.useEffect(() => {
    if (!greetingTemplate?.name) return;
    setTestTemplateName(greetingTemplate.name);
    setTestTemplateLanguage(greetingTemplate.language || DEFAULT_TEST_TEMPLATE_LANGUAGE);
  }, [greetingTemplate?.language, greetingTemplate?.name]);

  const refreshTemplateCatalog = React.useCallback(async () => {
    if (!lastWsApiSettings?.whatsappId) return;
    setLoadingTemplateCatalog(true);
    try {
      const result = await metaOnboardingManager.listTemplates();
      const templates = result || [];
      setTemplateCatalog(templates);
      const greeting =
        templates.find((template) => template.name === DEFAULT_TEST_TEMPLATE_NAME) ||
        null;
      if (greeting) {
        setGreetingTemplate(greeting);
      }
    } catch {
      setTemplateCatalog((previous) => previous);
    } finally {
      setLoadingTemplateCatalog(false);
    }
  }, [lastWsApiSettings?.whatsappId]);

  React.useEffect(() => {
    if (!lastWsApiSettings?.whatsappId) return;
    refreshTemplateCatalog();
  }, [lastWsApiSettings?.whatsappId, refreshTemplateCatalog]);

  const handleConnectWhatsApp = async (mode: WhatsAppOnboardingMode) => {
    setConnectDialogOpen(false);
    setConnectingWhatsApp(true);
    let signupTimeoutId: number | undefined;
    let resolveSignupPayload: (value: EmbeddedSignupPayload) => void;
    let rejectSignupPayload: (reason: Error) => void;
    const signupPayloadPromise = new Promise<EmbeddedSignupPayload>(
      (resolve, reject) => {
        resolveSignupPayload = resolve;
        rejectSignupPayload = reject;
        signupTimeoutId = window.setTimeout(() => {
          reject(
            new Error(
              "Meta completo el login, pero no devolvio WABA ID ni Phone Number ID. Revisa que el registro insertado termine en Finish.",
            ),
          );
        }, META_LOGIN_TIMEOUT_MS);
      },
    );

    const messageHandler = (event: MessageEvent) => {
      if (!isTrustedFacebookOrigin(event.origin)) return;
      const data = parseMaybeJson(event.data) as EmbeddedSignupMessage;
      if (data?.type !== "WA_EMBEDDED_SIGNUP") return;
      if (!isEmbeddedSignupFinish(data)) return;

      const payload = readEmbeddedSignupPayload(data);
      if (!payload.wabaId || !payload.phoneNumberId) {
        rejectSignupPayload(
          new Error(
            "Meta finalizo el registro, pero no envío WABA ID o Phone Number ID.",
          ),
        );
        return;
      }
      resolveSignupPayload(payload);
    };

    window.addEventListener("message", messageHandler);
    try {
      const config = await metaOnboardingManager.getConfig();
      if (!config?.appId || !config.embeddedSignupConfigId) {
        throw new Error("Falta configurar META_APP_ID o META_EMBEDDED_SIGNUP_CONFIG_ID");
      }

      await loadFacebookSdk(config);

      const loginResponsePromise = new Promise<any>((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          reject(
            new Error(
              "Meta no completo el registro insertado. Cierra el popup y vuelve a intentarlo.",
            ),
          );
        }, META_LOGIN_TIMEOUT_MS);

        const extras =
          mode === "coexistence"
            ? {
                setup: {},
                featureType: "whatsapp_business_app_onboarding",
                sessionInfoVersion: "3",
                version: "v4",
              }
            : {
                feature: "whatsapp_embedded_signup",
                sessionInfoVersion: "3",
                version: "v4",
                setup: {},
              };

        window.FB.login(
          (response: any) => {
            window.clearTimeout(timeoutId);
            resolve(response);
          },
          {
            config_id: config.embeddedSignupConfigId,
            response_type: "code",
            override_default_response_type: true,
            extras,
          },
        );
      });

      const [loginResponse, signupPayload] = await Promise.all([
        loginResponsePromise,
        signupPayloadPromise,
      ]);
      const code = loginResponse?.authResponse?.code;
      if (!code) {
        throw new Error("Meta no devolvio el código de autorización");
      }
      if (!signupPayload.wabaId || !signupPayload.phoneNumberId) {
        throw new Error("Meta no devolvio WABA ID o Phone Number ID");
      }

      const result = await metaOnboardingManager.completeWhatsapp({
        code,
        wabaId: signupPayload.wabaId,
        phoneNumberId: signupPayload.phoneNumberId,
        businessId: signupPayload.businessId,
        onboardingType: mode === "coexistence" ? "COEXISTENCE" : "STANDARD",
        rawSignupPayload: {
          ...signupPayload.rawSignupPayload,
          lyproOnboardingMode: mode,
        },
      });
      setMetaWhatsappStatus(result?.whatsapp);
      await refreshAllData();
      setSnackbarMessage("WhatsApp conectado correctamente");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      await refreshMetaWhatsappStatus();
      setSnackbarMessage(error?.message || "No se pudo conectar con Meta");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      if (signupTimeoutId) window.clearTimeout(signupTimeoutId);
      window.removeEventListener("message", messageHandler);
      setConnectingWhatsApp(false);
    }
  };

  const handleResetWhatsApp = async () => {
    setResetDialogOpen(false);
    setResettingWhatsApp(true);
    try {
      const result = await metaOnboardingManager.resetWhatsapp();
      setMetaWhatsappStatus(result?.whatsapp);
      setRegistrationPin("");
      await refreshAllData();
      setSnackbarMessage("Conexión de WhatsApp reiniciada en LyPro");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      setSnackbarMessage(error?.message || "No se pudo reiniciar WhatsApp");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setResettingWhatsApp(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!selectedTemplateApproved) {
      setSnackbarMessage("La plantilla debe estar aprobada por Meta antes de enviar la prueba.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (!testPhoneNumber.trim()) {
      setSnackbarMessage("Ingresa el número destino antes de enviar la plantilla.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    setSendingTestMessage(true);
    try {
      await metaOnboardingManager.sendTemplate({
        to: testPhoneNumber,
        templateName: testTemplateName || DEFAULT_TEST_TEMPLATE_NAME,
        templateLanguage: testTemplateLanguage || DEFAULT_TEST_TEMPLATE_LANGUAGE,
      });
      setSnackbarMessage("Plantilla de prueba enviada.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTestTemplateDialogOpen(false);
    } catch (error: any) {
      setSnackbarMessage(error?.message || "No se pudo enviar la plantilla.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSendingTestMessage(false);
    }
  };

  const handleCreateGreetingTemplate = async () => {
    setCreatingGreetingTemplate(true);
    try {
      const result = await metaOnboardingManager.createGreetingTemplate();
      setGreetingTemplate(result || null);
      await refreshTemplateCatalog();
      setTestTemplateName(result?.name || testTemplateName);
      setTestTemplateLanguage(result?.language || testTemplateLanguage);
      setSnackbarMessage("Plantilla de saludo enviada a revisión de Meta.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      setSnackbarMessage(error?.message || "No se pudo crear la plantilla de saludo");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setCreatingGreetingTemplate(false);
    }
  };

  const handleTemplateDraftChange = (
    key: keyof MetaWhatsappTemplateCreateRequest,
    value: string,
  ) => {
    setTemplateDraft((previous) => ({
      ...previous,
      [key]:
        key === "buttons"
          ? value
              .split("\n")
              .map((item) => item.trim())
              .filter(Boolean)
          : value,
    }));
  };

  const handleCreateTemplate = async () => {
    setCreatingTemplate(true);
    try {
      const result = await metaOnboardingManager.createTemplate({
        bodyExamples: templateDraft.bodyExamples,
        name: templateDraft.name.trim(),
        language: (templateDraft.language || "es").trim(),
        category: templateDraft.category || "UTILITY",
        headerText: templateDraft.headerText?.trim() || undefined,
        bodyText: templateDraft.bodyText.trim(),
        footerText: templateDraft.footerText?.trim() || undefined,
        buttons: (templateDraft.buttons || []).filter(Boolean),
      });
      await refreshTemplateCatalog();
      if (result?.name) {
        setTestTemplateName(result.name);
        setTestTemplateLanguage(result.language || "es");
      }
      setTemplateDraft(emptyTemplateDraft);
      setCreateTemplateDialogOpen(false);
      setSnackbarMessage("Plantilla enviada a revisión de Meta.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      setSnackbarMessage(error?.message || "No se pudo crear la plantilla");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setCreatingTemplate(false);
    }
  };

  const handleOpenTemplateSendDialog = (template: MetaWhatsappTemplate) => {
    setTestTemplateName(template.name);
    setTestTemplateLanguage(template.language || "es");
    setTestTemplateDialogOpen(true);
  };

  const handleBusinessProfileChange = (
    key: keyof MetaWhatsappBusinessProfile,
    value: string,
  ) => {
    setBusinessProfile((previous) => ({
      ...previous,
      [key]: key === "websites" ? value.split("\n").map((item) => item.trim()) : value,
    }));
  };

  const handleSaveBusinessProfile = async () => {
    setSavingBusinessProfile(true);
    try {
      const result = await metaOnboardingManager.updateBusinessProfile({
        about: businessProfile.about,
        address: businessProfile.address,
        description: businessProfile.description,
        email: businessProfile.email,
        websites: (businessProfile.websites || []).filter(Boolean),
        vertical: businessProfile.vertical,
        profilePictureHandle: businessProfile.profilePictureHandle,
      });
      setMetaWhatsappStatus(result?.whatsapp);
      setBusinessProfile({
        ...emptyBusinessProfile,
        ...(result?.whatsapp?.businessProfile || businessProfile),
        websites:
          result?.whatsapp?.businessProfile?.websites ||
          businessProfile.websites ||
          [],
      });
      setSnackbarMessage("Perfil de WhatsApp actualizado");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      setSnackbarMessage(error?.message || "No se pudo actualizar el perfil");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSavingBusinessProfile(false);
    }
  };

  const handleUploadProfileLogo = async (file: File) => {
    setUploadingProfileLogo(true);
    try {
      const logoUrl = await profileLogoStorage.uploadAssetAndGetUrl(
        file,
        "whatsapp-profile",
      );
      const result = await metaOnboardingManager.updateBusinessProfile({
        about: businessProfile.about,
        address: businessProfile.address,
        description: businessProfile.description,
        email: businessProfile.email,
        websites: (businessProfile.websites || []).filter(Boolean),
        vertical: businessProfile.vertical,
        profilePictureSourceUrl: logoUrl,
      });
      setMetaWhatsappStatus(result?.whatsapp);
      setBusinessProfile({
        ...emptyBusinessProfile,
        ...(result?.whatsapp?.businessProfile || businessProfile),
        profilePictureSourceUrl: logoUrl,
        websites:
          result?.whatsapp?.businessProfile?.websites ||
          businessProfile.websites ||
          [],
      });
      setSnackbarMessage("Logo de WhatsApp actualizado");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      setSnackbarMessage(error?.message || "No se pudo subir el logo a WhatsApp");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setUploadingProfileLogo(false);
    }
  };

  const handleValidateCloudApi = async () => {
    setValidatingCloudApi(true);
    try {
      const result = await metaOnboardingManager.validateCloudApi();
      setMetaWhatsappStatus(result?.whatsapp);
      await refreshAllData();
      setSnackbarMessage("WhatsApp Cloud API validada correctamente");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      await refreshMetaWhatsappStatus();
      setSnackbarMessage(error?.message || "No se pudo validar WhatsApp Cloud API");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setValidatingCloudApi(false);
    }
  };

  const handleRegisterPhone = async () => {
    setRegisteringPhone(true);
    try {
      const result = await metaOnboardingManager.registerPhone({
        pin: registrationPin,
      });
      setMetaWhatsappStatus(result?.whatsapp);
      setRegistrationPin("");
      await refreshAllData();
      setSnackbarMessage("Número registrado en WhatsApp Cloud API");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      await refreshMetaWhatsappStatus();
      setSnackbarMessage(
        error?.message || "No se pudo registrar el número en WhatsApp Cloud API",
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setRegisteringPhone(false);
    }
  };

  const whatsappStatus = loadingMetaStatus
    ? "CONSULTANDO"
    : metaWhatsappStatus?.status || "NOT_CONNECTED";
  const whatsappConnected =
    whatsappStatus === "CONNECTED" || Boolean(lastWsApiSettings?.phoneNumberId);
  const canResetWhatsapp = Boolean(
    metaWhatsappStatus?.status === "ERROR" ||
      metaWhatsappStatus?.status === "PENDING" ||
      lastWsApiSettings?.accessToken ||
      lastWsApiSettings?.phoneNumberId ||
      lastWsApiSettings?.whatsappId,
  );
  const canValidateCloudApi = Boolean(
    lastWsApiSettings?.accessToken &&
      lastWsApiSettings?.phoneNumberId &&
      lastWsApiSettings?.whatsappId,
  );
  const canRegisterPhone =
    canValidateCloudApi && /^\d{6}$/.test(registrationPin.trim());
  const metaErrorHint = metaWhatsappStatus?.errorMessage
    ?.toLowerCase()
    .includes("client secret")
    ? "Revisa META_APP_SECRET en el API que esta usando esta pantalla. Debe ser el App Secret de la misma app usada por Embedded Signup."
    : "";
  const whatsappStatusColor = whatsappConnected
    ? "success"
    : whatsappStatus === "ERROR"
    ? "error"
    : "default";
  const whatsappStatusLabel = META_STATUS_LABELS[whatsappStatus] || whatsappStatus;
  const displayPhone =
    metaWhatsappStatus?.displayPhoneNumber ||
    lastWsApiSettings?.phoneNumberId ||
    metaWhatsappStatus?.phoneNumberId ||
    "";
  const hasCloudCredentials = canValidateCloudApi;
  const isCoexistenceConnection = metaWhatsappStatus?.onboardingType === "COEXISTENCE";
  const cloudApiValidated = Boolean(metaWhatsappStatus?.cloudApiValidatedAt);
  const phoneRegistrationKnown = Boolean(
    isCoexistenceConnection ||
    metaWhatsappStatus?.phoneRegisteredAt ||
      ["VERIFIED", "CONNECTED", "APPROVED"].includes(
        String(metaWhatsappStatus?.phoneRegistrationStatus || "").toUpperCase(),
      ),
  );
  const templateApproved = greetingTemplate?.status === "APPROVED";
  const templateStatusLabel =
    META_TEMPLATE_STATUS_LABELS[greetingTemplate?.status || ""] ||
    greetingTemplate?.status ||
    "Sin crear";
  const selectedTemplate =
    templateCatalog.find((template) => template.name === testTemplateName) ||
    greetingTemplate ||
    null;
  const selectedTemplateApproved = selectedTemplate?.status === "APPROVED";
  const filteredTemplateCatalog = React.useMemo(() => {
    const query = templateSearch.trim().toLowerCase();
    return templateCatalog.filter((template) => {
      const matchesQuery = !query || [
        template.name,
        template.language,
        template.category,
        ...(template.components || []).map((component) => component.text || ""),
      ].some((value) => String(value || "").toLowerCase().includes(query));
      const matchesStatus = templateStatusFilter === "ALL" || template.status === templateStatusFilter;
      const matchesCategory = templateCategoryFilter === "ALL" || template.category === templateCategoryFilter;
      return matchesQuery && matchesStatus && matchesCategory;
    });
  }, [templateCatalog, templateSearch, templateStatusFilter, templateCategoryFilter]);

  const handleTemplateButtonChange = (index: number, value: string) => {
    setTemplateDraft((previous) => {
      const buttons = [...(previous.buttons || [])];
      buttons[index] = value;
      return { ...previous, buttons: buttons.slice(0, 3) };
    });
  };
  const missingPaymentMethod =
    metaWhatsappStatus?.billingStatus === "MISSING_PAYMENT_METHOD" ||
    String(metaWhatsappStatus?.errorMessage || "").toLowerCase().includes("payment") ||
    String(metaWhatsappStatus?.errorMessage || "").toLowerCase().includes("pago");
  const onboardingSteps: Array<{
    title: string;
    description: string;
    status: CloudApiStepStatus;
    action?: React.ReactNode;
  }> = [
    {
      title: "Autorizar negocio en Meta",
      description:
        "Selecciona o crea el negocio, el WABA y el número que vas a operar desde LyPro.",
      status: whatsappStatus === "ERROR" ? "error" : hasCloudCredentials ? "done" : "current",
      action: !hasCloudCredentials ? (
        <Button
          variant="contained"
          color="primary"
          onClick={() => setConnectDialogOpen(true)}
          disabled={connectingWhatsApp || resettingWhatsApp}
          startIcon={connectingWhatsApp ? <CircularProgress size={18} /> : <WhatsApp />}
          sx={{ minWidth: { xs: "100%", sm: 190 } }}
        >
          {connectingWhatsApp ? "Conectando" : "Conectar con Meta"}
        </Button>
      ) : undefined,
    },
    {
      title: "Validar Cloud API",
      description:
        "Confirmamos que el token, WABA ID y Phone Number ID funcionan antes de registrar el número.",
      status: hasCloudCredentials
        ? cloudApiValidated
          ? "done"
          : "current"
        : "pending",
      action: hasCloudCredentials && !cloudApiValidated ? (
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleValidateCloudApi}
          disabled={validatingCloudApi}
          startIcon={validatingCloudApi ? <CircularProgress size={16} /> : <FactCheck />}
          sx={{ minWidth: { xs: "100%", sm: 190 } }}
        >
          {validatingCloudApi ? "Validando" : "Validar Cloud API"}
        </Button>
      ) : undefined,
    },
    {
      title: isCoexistenceConnection
        ? "Confirmar coexistencia"
        : "Registrar número de teléfono",
      description: isCoexistenceConnection
        ? "El número permanece activo en WhatsApp Business App y no requiere registro manual con PIN."
        : "Este paso queda pendiente hasta ingresar el PIN de 6 dígitos de verificación de WhatsApp.",
      status: phoneRegistrationKnown ? "done" : hasCloudCredentials ? "current" : "pending",
      action: hasCloudCredentials && !phoneRegistrationKnown ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: "100%" }}>
          <TextField
            label="PIN de 6 dígitos"
            value={registrationPin}
            onChange={(event) =>
              setRegistrationPin(event.target.value.replace(/[^\d]/g, "").slice(0, 6))
            }
            type="password"
            inputProps={{ inputMode: "numeric", maxLength: 6 }}
            size="small"
            fullWidth
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleRegisterPhone}
            disabled={!canRegisterPhone || registeringPhone}
            startIcon={registeringPhone ? <CircularProgress size={16} /> : <CheckCircle />}
            sx={{ minWidth: { xs: "100%", sm: 190 } }}
          >
            {registeringPhone ? "Registrando" : "Registrar número"}
          </Button>
        </Stack>
      ) : undefined,
    },
    {
      title: "Enviar plantilla de prueba",
      description:
        "Crea una plantilla de saludo, espera aprobación de Meta y luego envía una prueba.",
      status: templateApproved ? "current" : phoneRegistrationKnown ? "current" : "pending",
      action: phoneRegistrationKnown ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: "100%" }}>
          <Button
            variant={greetingTemplate ? "outlined" : "contained"}
            color="primary"
            onClick={handleCreateGreetingTemplate}
            disabled={creatingGreetingTemplate || loadingGreetingTemplate}
            startIcon={creatingGreetingTemplate ? <CircularProgress size={16} /> : <CheckCircle />}
            sx={{ minWidth: { xs: "100%", sm: 190 } }}
          >
            {greetingTemplate ? "Revisar plantilla" : "Crear plantilla"}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setTestTemplateDialogOpen(true)}
            disabled={!templateApproved}
            startIcon={<Send />}
            sx={{ minWidth: { xs: "100%", sm: 190 } }}
          >
            Enviar prueba
          </Button>
        </Stack>
      ) : undefined,
    },
  ];
  const profileWebsites = (businessProfile.websites || []).join("\n");
  function renderSettingsField<T extends AISettings | WSSettings>({
    scope,
    field,
    value,
    originalValue,
    onChange,
  }: {
    scope: "ai" | "ws";
    field: SettingsFieldConfig<T>;
    value: string | undefined;
    originalValue: string | undefined;
    onChange: (value: string) => void;
  }) {
    const fieldId = `${scope}.${String(field.key)}`;
    const isSensitive = field.sensitive ?? false;
    const hasStoredValue = Boolean(originalValue?.trim());
    const isLocked = Boolean(field.lockWhenHasValue && hasStoredValue);
    const isEditingSensitive = sensitiveEditing[fieldId] ?? false;
    const displayValue = isLocked
      ? isSensitive
        ? SENSITIVE_MASK
        : originalValue ?? ""
      : isSensitive
      ? isEditingSensitive
        ? value ?? ""
        : value
          ? SENSITIVE_MASK
          : ""
      : value ?? "";
    const helperText = isLocked
      ? field.helperText
        ? `${field.helperText} ${LOCKED_FIELD_HELPER_TEXT}`
        : LOCKED_FIELD_HELPER_TEXT
      : field.helperText;

    return (
      <TextField
        key={fieldId}
        fullWidth
        label={field.label}
        variant="outlined"
        margin="normal"
        type={isSensitive && !isLocked && isEditingSensitive ? "password" : "text"}
        value={displayValue}
        name={`lypro-${fieldId.replace(".", "-")}`}
        disabled={isLocked}
        onFocus={() => {
          if (!isSensitive || isEditingSensitive || isLocked) return;

          setSensitiveBackups((previous) => ({
            ...previous,
            [fieldId]: value ?? "",
          }));
          setSensitiveEditing((previous) => ({
            ...previous,
            [fieldId]: true,
          }));

          if (value) {
            onChange("");
          }
        }}
        onBlur={() => {
          if (!isSensitive || isLocked) return;

          const backupValue = sensitiveBackups[fieldId] ?? originalValue ?? "";
          if ((value ?? "") === "" && backupValue) {
            onChange(backupValue);
          }

          setSensitiveEditing((previous) => ({
            ...previous,
            [fieldId]: false,
          }));
        }}
        onChange={(e) => onChange(e.target.value)}
        multiline={field.multiline}
        minRows={field.multiline ? (field.rows ?? 4) : undefined}
        helperText={helperText}
        autoComplete={isSensitive ? "new-password" : "off"}
        inputProps={{
          autoComplete: isSensitive ? "new-password" : "off",
          "data-lpignore": "true",
        }}
      />
    );
  }

  const schedule = normalizeSchedule(aiSettings?.schedule);
  const hasMaposIntegration = aiSettings?.companyType === "MAPOS";
  const hasMaposNotificationsBot = aiSettings?.allowedIntegrations.includes(
    IntegrationCodeEnum.LYPRO_MAPOS_NOTIFICATIONS_BOT,
  ) === true;
  const hasLoansWorkflow = aiSettings?.agentWorkflow === "LOANS";
  const isProfileTab = !cloudApiOnly && tabIndex === 0;
  const isMenuTab = !cloudApiOnly && hasMaposIntegration && tabIndex === 1;
  const isHoursTab = !cloudApiOnly && tabIndex === 2;
  const isOrdersTab = !cloudApiOnly && hasMaposIntegration && tabIndex === 3;
  const isWhatsAppTab = cloudApiOnly;
  const isProductsTab = !cloudApiOnly && hasMaposIntegration && tabIndex === 4;
  const isBanchatTab = !cloudApiOnly && hasLoansWorkflow && tabIndex === 5;
  const isCloudConnectTab = cloudApiTab === "connect";
  const isCloudProfileTab = cloudApiTab === "profile";
  const isCloudTemplatesTab = cloudApiTab === "templates";
  const isCloudConfigTab = cloudApiTab === "config";

  return (
    <Box
      sx={{
        ...dashboardPageSx,
        minHeight: { xs: "calc(100dvh - 56px)", md: "calc(100dvh - 64px)" },
        ...dashboardPagePaddingSx,
        pb: 2,
      }}
    >
      {/* <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          Integración Mapos
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Organiza el perfil, el menu, los horarios, los pedidos, WhatsApp Business Platform y
          los productos desde aquí.
        </Typography>
      </Box> */}
      {/* <Alert severity="info" sx={{ mt: 2 }}>
        Company ID, ID Mapos, Access Token, Phone Number ID y WhatsApp ID se
        bloquean una vez que tienen valor guardado.
      </Alert> */}

      {!cloudApiOnly && (
      <ToggleButtonGroup
        value={tabIndex}
        exclusive
        size="small"
        onChange={(_event, nextValue) => {
          if (nextValue !== null) changeTab(_event, nextValue);
        }}
        sx={{
          p: 0.5,
          mb: 1.5,
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          bgcolor: "background.paper",
          "& .MuiToggleButtonGroup-grouped": {
            border: 0,
            borderRadius: "8px !important",
            mx: 0,
          },
          "& .MuiToggleButton-root": {
            flex: "0 0 auto",
            px: { xs: 1.25, sm: 1.75 },
            minHeight: 38,
            textTransform: "none",
            gap: 0.75,
          },
        }}
      >
        <ToggleButton value={0}><Person fontSize="small" />Perfil</ToggleButton>
        {hasMaposIntegration && (
          <ToggleButton value={1}><MenuBook fontSize="small" />Menu</ToggleButton>
        )}
        <ToggleButton value={2}><AccessTime fontSize="small" />Horarios</ToggleButton>
        {hasMaposIntegration && (
          <ToggleButton value={3}><ShoppingBag fontSize="small" />Pedidos</ToggleButton>
        )}
        {hasMaposIntegration && (
          <ToggleButton value={4}><Category fontSize="small" />Productos</ToggleButton>
        )}
        {hasLoansWorkflow && (
          <ToggleButton value={5}><AccountTree fontSize="small" />Conexión de créditos</ToggleButton>
        )}
      </ToggleButtonGroup>
      )}

      {aiSettings === undefined ? (
        <PageLoadingState label="Cargando configuración" compact />
      ) : (
        <Box sx={{ mt: 0 }}>
          {isProfileTab && (
            <Card sx={dashboardCardSx}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={dashboardSectionTitleSx}>
                  Perfil
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  Datos base del negocio, el bot y la identidad de la
                  integración.
                </Typography>
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Modo de atención del bot
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                    Elige atención humana o activa un workflow especializado.
                  </Typography>
                  <ToggleButtonGroup
                    value={aiSettings.agentWorkflow}
                    exclusive
                    onChange={(_event, nextValue) => {
                      if (nextValue !== null) {
                        aiSettingsFieldChange("agentWorkflow", nextValue);
                      }
                    }}
                    sx={{ flexWrap: "wrap" }}
                  >
                    <ToggleButton value="NONE" sx={{ gap: 0.75, textTransform: "none" }}>
                      <SmartToy fontSize="small" />
                      Conversacion normal
                    </ToggleButton>
                    {aiSettings.allowedWorkflows.includes("MAPOS_RESTAURANT") && (
                      <ToggleButton value="MAPOS_RESTAURANT" sx={{ gap: 0.75, textTransform: "none" }}>
                        <AccountTree fontSize="small" />
                        {WORKFLOW_LABELS.MAPOS_RESTAURANT}
                      </ToggleButton>
                    )}
                    {aiSettings.allowedWorkflows.includes("LOANS") && (
                      <ToggleButton value="LOANS" sx={{ gap: 0.75, textTransform: "none" }}>
                        <AccountTree fontSize="small" />
                        {WORKFLOW_LABELS.LOANS}
                      </ToggleButton>
                    )}
                    {aiSettings.allowedWorkflows.includes("EXTERNAL_BOT") && (
                      <ToggleButton value="EXTERNAL_BOT" sx={{ gap: 0.75, textTransform: "none" }}>
                        <AccountTree fontSize="small" />
                        {WORKFLOW_LABELS.EXTERNAL_BOT}
                      </ToggleButton>
                    )}
                  </ToggleButtonGroup>
                  <Typography variant="caption" display="block" sx={{ color: "text.secondary", mt: 1 }}>
                    {aiSettings.agentWorkflow === "MAPOS_RESTAURANT"
                      ? "El bot puede usar el flujo especializado para atender pedidos del restaurante."
                      : aiSettings.agentWorkflow === "LOANS"
                        ? "Atención de créditos. Cada compañía requiere su propia conexión, marca y plantillas."
                        : aiSettings.agentWorkflow === "EXTERNAL_BOT"
                          ? "LyPro guardará los mensajes entrantes, los enviará al bot externo configurado y entregará sus respuestas al cliente por WhatsApp. En este modo no responde el bot interno de LyPro."
                        : "Las conversaciones entrantes quedan asignadas a agentes humanos sin respuesta automática de AI."}
                  </Typography>
                  {aiSettings.agentWorkflow === "LOANS" && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">Aviso genérico para abrir la conversación</Typography>
                      <Alert severity="info" sx={{ mb: 1 }}>
                        BanChat envía el evento collection_reminder y LyPro usa una sola plantilla aprobada para iniciar el chat:
                        “Hola. Tienes nuevas notificaciones”. El botón “Revisar notificaciones” abre la atención del bot de Loans.
                        No se incluyen datos de pagos en la plantilla. Después de que la persona responde, el bot consulta BanChat.
                        Meta debe aprobar la plantilla y el envío sigue sujeto al consentimiento y a las verificaciones configuradas.
                      </Alert>
                      {templateCatalog.filter(template => template.name === 'banchat_aviso_generico' && template.language === 'es').map(template => (
                        <Box key={`${template.id}-${template.language}`} sx={{ mb: 1 }}>
                          <Typography variant="body2">{template.name} · {template.language} · {template.status}</Typography>
                          <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
                            ID: {template.id || 'Sin ID'} · Versión: {template.definitionHash || 'Sincroniza el catálogo'}
                          </Typography>
                        </Box>
                      ))}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Prepara únicamente esta plantilla y envíala a revisión de Meta. Crear o aprobar la plantilla no activa por sí solo los envíos automáticos.
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {LOANS_TEMPLATE_DRAFTS.map((draft) => {
                          const existing = templateCatalog.find((template) => template.name === draft.name && template.language === "es");
                          return (
                            <Button key={draft.name} size="small" variant="outlined" disabled={Boolean(existing)}
                              onClick={() => {
                                setTemplateDraft({ ...emptyTemplateDraft, name: draft.name, language: "es", category: "UTILITY", bodyText: draft.bodyText,
                                  bodyExamples: undefined,
                                  buttons: [...draft.buttons] });
                                setCreateTemplateDialogOpen(true);
                              }}>
                              {draft.label}{existing ? ` (${existing.status})` : " · Preparar"}
                            </Button>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}
                </Box>
                <Divider sx={{ my: 2 }} />
                {AI_PROFILE_FIELDS.filter(
                  (field) =>
                    (aiSettings.companyType === "MAPOS" || field.key !== "idMapos") &&
                    (aiSettings.companyType === "MAPOS" ||
                      (field.key !== "recommendations" && field.key !== "synonyms")),
                ).map((field) =>
                  renderSettingsField({
                    scope: "ai",
                    field,
                    value:
                      aiSettings[field.key] === undefined
                        ? ""
                        : String(aiSettings[field.key]),
                    originalValue:
                      lastAISettings?.[field.key] === undefined
                        ? ""
                        : String(lastAISettings[field.key]),
                    onChange: (nextValue) =>
                      aiSettingsFieldChange(field.key, nextValue),
                  }),
                )}
                {hasAISettingsChanges && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={openAISettingsDialog}
                  >
                    Actualizar
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {isBanchatTab && <BanchatConnectionSettings />}

          {isMenuTab && (
            <Card sx={dashboardCardSx}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={dashboardSectionTitleSx}>
                  Menu
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  Configura la carta principal y las imágenes de apoyo.
                </Typography>

                {renderSettingsField({
                  scope: "ai",
                  field: {
                    key: "urlMenu",
                    label: "URL principal del menu",
                  } as SettingsFieldConfig<AISettings>,
                  value: aiSettings.urlMenu ? String(aiSettings.urlMenu) : "",
                  originalValue: lastAISettings?.urlMenu
                    ? String(lastAISettings.urlMenu)
                    : "",
                  onChange: (nextValue) => aiSettingsFieldChange("urlMenu", nextValue),
                })}

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Cartas (máximo {MAX_IMAGES} imágenes)
                  </Typography>
                  {companyId ? (
                    <MenuImageUploader
                      images={images}
                      onAddImages={handleAddImages}
                      onRemoveImage={handleRemoveImage}
                      loading={loading}
                    />
                  ) : (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Completa el Company ID para administrar cartas desde aquí.
                    </Alert>
                  )}
                  {loading && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Cargando imágenes...
                    </Alert>
                  )}
                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  )}
                  {progress > 0 && (
                    <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                      Progreso de carga: {Math.round(progress)}%
                    </Typography>
                  )}
                </Box>

                {hasAISettingsChanges && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={openAISettingsDialog}
                  >
                    Actualizar
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {isHoursTab && (
            <Card sx={dashboardCardSx}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={dashboardSectionTitleSx}>
                  Horarios
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  Define si el horario es fijo para toda la semana o variable por
                  día.
                </Typography>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="schedule-mode-label">Tipo de horario</InputLabel>
                  <Select
                    labelId="schedule-mode-label"
                    value={schedule.mode}
                    label="Tipo de horario"
                    onChange={(e) => {
                      const nextMode = e.target.value as "fixed" | "variable";
                      if (nextMode === "fixed") {
                        aiSettingsFieldChange("schedule" as keyof AISettings, {
                          mode: "fixed",
                          fixed:
                            schedule.mode === "fixed"
                              ? schedule.fixed
                              : DEFAULT_FIXED_SCHEDULE,
                        });
                        return;
                      }

                      aiSettingsFieldChange("schedule" as keyof AISettings, {
                        mode: "variable",
                        days:
                          schedule.mode === "variable"
                            ? schedule.days
                            : { ...DEFAULT_VARIABLE_DAYS },
                      });
                    }}
                  >
                    <MenuItem value="fixed">Fijo para todos los días</MenuItem>
                    <MenuItem value="variable">Variable por día</MenuItem>
                  </Select>
                </FormControl>

                {schedule.mode === "fixed" && (
                  <Box
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={2}
                    mt={2}
                  >
                    <TextField
                      label="Hora de apertura"
                      type="time"
                      fullWidth
                      slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { step: 60 },
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <AccessTime sx={{ color: "white" }} />
                            </InputAdornment>
                          ),
                        },
                      }}
                      sx={{
                        "& input::-webkit-calendar-picker-indicator": {
                          display: "none",
                        },
                      }}
                      value={schedule.fixed.opening}
                      onChange={(e) => {
                        const opening = e.target.value;
                        if (!opening) return;
                        aiSettingsFieldChange("schedule" as keyof AISettings, {
                          mode: "fixed",
                          fixed: {
                            ...schedule.fixed,
                            opening,
                          },
                        });
                      }}
                    />
                    <TextField
                      label="Hora de cierre"
                      type="time"
                      fullWidth
                      slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { step: 60 },
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <AccessTime sx={{ color: "white" }} />
                            </InputAdornment>
                          ),
                        },
                      }}
                      sx={{
                        "& input::-webkit-calendar-picker-indicator": {
                          display: "none",
                        },
                      }}
                      value={schedule.fixed.closing}
                      onChange={(e) => {
                        const closing = e.target.value;
                        if (!closing) return;
                        aiSettingsFieldChange("schedule" as keyof AISettings, {
                          mode: "fixed",
                          fixed: {
                            ...schedule.fixed,
                            closing,
                          },
                        });
                      }}
                    />
                  </Box>
                )}

                {schedule.mode === "variable" && (
                  <Box mt={2}>
                    {[
                      "lunes",
                      "martes",
                      "miercoles",
                      "jueves",
                      "viernes",
                      "sabado",
                      "domingo",
                    ].map((day) => {
                      const hourDay = schedule.days[day] ?? null;
                      const isOpen = hourDay !== null;

                      return (
                        <Box
                          key={day}
                          display="flex"
                          flexDirection={{ xs: "column", sm: "row" }}
                          alignItems="center"
                          gap={2}
                          mb={2}
                          sx={{
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            pb: 1,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isOpen}
                                onChange={(e) => {
                                  aiSettingsFieldChange("schedule" as keyof AISettings, {
                                    mode: "variable",
                                    days: {
                                      ...schedule.days,
                                      [day]: e.target.checked
                                        ? { opening: "08:00", closing: "21:00" }
                                        : null,
                                    },
                                  });
                                }}
                              />
                            }
                            label={toTitleCase(day)}
                            sx={{ minWidth: { xs: "100%", sm: 120 } }}
                          />

                          <TextField
                            label="Apertura"
                            type="time"
                            fullWidth
                            disabled={!isOpen}
                            slotProps={{
                              inputLabel: { shrink: true },
                              htmlInput: { step: 60 },
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <AccessTime sx={{ color: "white" }} />
                                  </InputAdornment>
                                ),
                              },
                            }}
                            sx={{
                              "& input::-webkit-calendar-picker-indicator": {
                                display: "none",
                              },
                            }}
                            value={hourDay?.opening ?? ""}
                            onChange={(e) => {
                              const opening = e.target.value;
                              aiSettingsFieldChange("schedule" as keyof AISettings, {
                                mode: "variable",
                                days: {
                                  ...schedule.days,
                                  [day]: {
                                    ...(schedule.days[day] ?? {
                                      opening: "08:00",
                                      closing: "21:00",
                                    }),
                                    opening,
                                  },
                                },
                              });
                            }}
                          />

                          <TextField
                            label="Cierre"
                            type="time"
                            fullWidth
                            disabled={!isOpen}
                            slotProps={{
                              inputLabel: { shrink: true },
                              htmlInput: { step: 60 },
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <AccessTime sx={{ color: "white" }} />
                                  </InputAdornment>
                                ),
                              },
                            }}
                            value={hourDay?.closing ?? ""}
                            onChange={(e) => {
                              const closing = e.target.value;
                              aiSettingsFieldChange("schedule" as keyof AISettings, {
                                mode: "variable",
                                days: {
                                  ...schedule.days,
                                  [day]: {
                                    ...(schedule.days[day] ?? {
                                      opening: "08:00",
                                      closing: "21:00",
                                    }),
                                    closing,
                                  },
                                },
                              });
                            }}
                            sx={{
                              "& input::-webkit-calendar-picker-indicator": {
                                display: "none",
                              },
                            }}
                          />

                          {isOpen && (
                            <Button
                              variant="outlined"
                              size="medium"
                              fullWidth
                              sx={{
                                px: 3,
                                py: 1,
                                fontSize: 13,
                                borderRadius: 1,
                                textTransform: "none",
                                color: "text.primary",
                                borderColor: "primary.main",
                                "&:hover": {
                                  backgroundColor: "primary.light",
                                  borderColor: "primary.main",
                                },
                              }}
                              onClick={() => {
                                const updatedDays = { ...schedule.days };
                                for (const otherDay of Object.keys(updatedDays)) {
                                  if (otherDay !== day && updatedDays[otherDay] !== null) {
                                    updatedDays[otherDay] = {
                                      opening: hourDay?.opening ?? "08:00",
                                      closing: hourDay?.closing ?? "21:00",
                                    };
                                  }
                                }
                                aiSettingsFieldChange("schedule" as keyof AISettings, {
                                  mode: "variable",
                                  days: updatedDays,
                                });
                              }}
                            >
                              Copiar a todos
                            </Button>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {hasAISettingsChanges && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={openAISettingsDialog}
                  >
                    Actualizar
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {isOrdersTab && (
            <Card sx={dashboardCardSx}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={dashboardSectionTitleSx}>
                  Pedidos
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  Ajusta como el asistente toma pedidos y que web debe usar para
                  redirigir.
                </Typography>

                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.default",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={aiSettings?.takeOrdersEnabled ?? true}
                        onChange={(e) =>
                          aiSettingsFieldChange(
                            "takeOrdersEnabled" as keyof AISettings,
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label={
                      (aiSettings?.takeOrdersEnabled ?? true)
                        ? "Pedidos habilitados"
                        : "Pedidos deshabilitados"
                    }
                  />
                  <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                    Si desactivas esta opción, el asistente dejará de tomar
                    pedidos y redirigira al usuario a la web oficial configurada.
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="Página web oficial"
                  variant="outlined"
                  margin="normal"
                  value={aiSettings?.websiteUrl ?? ""}
                  onChange={(e) =>
                    aiSettingsFieldChange("websiteUrl" as keyof AISettings, e.target.value)
                  }
                  placeholder="https://tudominio.com"
                  helperText="Se usa cuando la toma de pedidos esta deshabilitada."
                />

                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.default",
                  }}
                >
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                    Pedidos disponibles en la página web
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={aiSettings?.websitePickupEnabled ?? true}
                          onChange={(e) =>
                            aiSettingsFieldChange(
                              "websitePickupEnabled" as keyof AISettings,
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label={
                        (aiSettings?.websitePickupEnabled ?? true)
                          ? "Recogida habilitada"
                          : "Recogida deshabilitada"
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={aiSettings?.websiteDeliveryEnabled ?? true}
                          onChange={(e) =>
                            aiSettingsFieldChange(
                              "websiteDeliveryEnabled" as keyof AISettings,
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label={
                        (aiSettings?.websiteDeliveryEnabled ?? true)
                          ? "Domicilio habilitado"
                          : "Domicilio deshabilitado"
                      }
                    />
                  </Box>
                  <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                    Esto define que modalidades se anuncian cuando el bot redirige
                    al usuario a la página web oficial.
                  </Typography>
                  {!(aiSettings?.websitePickupEnabled ?? true) &&
                    !(aiSettings?.websiteDeliveryEnabled ?? true) && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        La página web quedará configurada sin recogida ni
                        domicilio.
                      </Alert>
                    )}
                </Box>

                {hasAISettingsChanges && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={openAISettingsDialog}
                  >
                    Actualizar
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {isWhatsAppTab && (
            <>
              <Box sx={{ mb: 1.5 }}>
                <ToggleButtonGroup
                  value={cloudApiTab}
                  exclusive
                  size="small"
                  onChange={(_event, nextValue) => {
                    if (nextValue) setCloudApiTab(nextValue);
                  }}
                  sx={{
                    p: 0.5,
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                    bgcolor: "background.paper",
                    "& .MuiToggleButtonGroup-grouped": {
                      border: 0,
                      borderRadius: "8px !important",
                      mx: 0,
                    },
                    "& .MuiToggleButton-root": {
                      flex: "0 0 auto",
                      px: { xs: 1.25, sm: 1.75 },
                      minHeight: 38,
                      gap: 0.75,
                      textTransform: "none",
                    },
                  }}
                >
                  <ToggleButton value="connect">
                    <WhatsApp fontSize="small" />
                    Conectar
                  </ToggleButton>
                  <ToggleButton value="profile">
                    <AccountCircle fontSize="small" />
                    Perfil WhatsApp
                  </ToggleButton>
                  <ToggleButton value="templates">
                    <Send fontSize="small" />
                    Plantillas
                  </ToggleButton>
                  <ToggleButton value="config">
                    <Settings fontSize="small" />
                    Configuración
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Card sx={dashboardCardSx}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="h6" sx={dashboardSectionTitleSx}>
                    WhatsApp Business Platform
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                    Registro insertado de Meta para WhatsApp Cloud API.
                  </Typography>

                  {isCloudConnectTab && (
                    <Stack spacing={2.5} sx={{ mt: 2.5 }}>
                      <Box
                        sx={(theme) => ({
                          p: { xs: 2, md: 2.5 },
                          border: "1px solid",
                          borderColor:
                            whatsappStatus === "ERROR"
                              ? "error.main"
                              : whatsappConnected
                                ? "success.main"
                                : "divider",
                          borderRadius: 1,
                          bgcolor:
                            whatsappStatus === "ERROR"
                              ? theme.palette.mode === "dark"
                                ? "rgba(211, 47, 47, 0.12)"
                                : "rgba(211, 47, 47, 0.04)"
                              : whatsappConnected
                                ? theme.palette.mode === "dark"
                                  ? "rgba(46, 125, 50, 0.12)"
                                  : "rgba(46, 125, 50, 0.04)"
                                : theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.03)"
                                  : "rgba(15, 23, 42, 0.02)",
                          boxShadow:
                            theme.palette.mode === "dark"
                              ? "inset 0 1px 0 rgba(255,255,255,0.04)"
                              : "inset 0 1px 0 rgba(255,255,255,0.7)",
                        })}
                      >
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between">
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box sx={{ width: 52, height: 52, borderRadius: 1, display: "grid", placeItems: "center", color: whatsappConnected ? "success.dark" : "primary.main", bgcolor: whatsappConnected ? "rgba(46, 125, 50, 0.09)" : "rgba(123, 31, 162, 0.09)" }}>
                              {loadingMetaStatus ? <CircularProgress size={24} /> : whatsappConnected ? <CloudDone /> : <WhatsApp />}
                            </Box>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                {whatsappConnected ? "WhatsApp vinculado" : "Conecta WhatsApp Cloud API"}
                              </Typography>
                              <Stack direction="row" spacing={1} alignItems="center" mt={0.75} flexWrap="wrap">
                                <Chip size="small" color={whatsappStatusColor} icon={whatsappStatus === "ERROR" ? <ErrorOutline /> : whatsappConnected ? <CheckCircle /> : undefined} label={whatsappStatusLabel} sx={{ fontWeight: 800 }} />
                                {displayPhone && <Chip size="small" variant="outlined" label={displayPhone} sx={{ fontWeight: 700 }} />}
                                {metaWhatsappStatus?.verifiedName && <Chip size="small" variant="outlined" icon={<Verified />} label={metaWhatsappStatus.verifiedName} />}
                                {missingPaymentMethod && <Chip size="small" color="warning" label="Pago pendiente" sx={{ fontWeight: 800 }} />}
                              </Stack>
                            </Box>
                          </Stack>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
                            <Button variant="outlined" color="inherit" onClick={() => (canValidateCloudApi ? handleValidateCloudApi() : refreshMetaWhatsappStatus())} disabled={loadingMetaStatus || validatingCloudApi} startIcon={loadingMetaStatus || validatingCloudApi ? <CircularProgress size={16} /> : <Refresh />} sx={{ minWidth: { xs: "100%", sm: 150 } }}>
                              {canValidateCloudApi ? "Consultar Meta" : "Actualizar"}
                            </Button>
                            {canResetWhatsapp && (
                              <Button variant="outlined" color="error" onClick={() => setResetDialogOpen(true)} disabled={resettingWhatsApp || connectingWhatsApp} startIcon={<LinkOff />} sx={{ minWidth: { xs: "100%", sm: 190 } }}>
                                Reiniciar conexión
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                        {whatsappStatus === "ERROR" && metaWhatsappStatus?.errorMessage && (
                          <Alert severity="error" sx={{ mt: 2 }}>
                            {metaWhatsappStatus.errorMessage}
                            {metaErrorHint && <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>{metaErrorHint}</Typography>}
                          </Alert>
                        )}
                        {missingPaymentMethod && (
                          <Alert
                            severity="warning"
                            sx={{ mt: 2 }}
                            action={
                              <Button
                                color="inherit"
                                size="small"
                                onClick={() => window.open(META_PAYMENT_SETTINGS_URL, "_blank", "noopener,noreferrer")}
                              >
                                Abrir Meta
                              </Button>
                            }
                          >
                            Falta configurar un método de pago en Meta para habilitar el envío de plantillas.
                          </Alert>
                        )}
                      </Box>

                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.1fr 0.9fr" }, gap: 2, alignItems: "start" }}>
                        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
                          <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Registro paso a paso</Typography>
                            <Typography variant="body2" color="text.secondary">Cada paso habilita el siguiente para que el usuario sepa que falta.</Typography>
                          </Box>
                          <Stack divider={<Divider flexItem />}>
                            {onboardingSteps.map((step, index) => {
                              const color = step.status === "done" ? "success.main" : step.status === "error" ? "error.main" : step.status === "current" ? "primary.main" : "text.disabled";
                              return (
                                <Box key={step.title} sx={{ p: 2 }}>
                                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                      <Box sx={{ width: 32, height: 32, borderRadius: 1, display: "grid", placeItems: "center", bgcolor: step.status === "done" ? "rgba(46, 125, 50, 0.1)" : step.status === "error" ? "rgba(211, 47, 47, 0.1)" : "action.hover", color, fontWeight: 900, flex: "0 0 auto" }}>
                                        {step.status === "done" ? <CheckCircle fontSize="small" /> : index + 1}
                                      </Box>
                                      <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{step.title}</Typography>
                                        <Typography variant="body2" color="text.secondary">{step.description}</Typography>
                                      </Box>
                                    </Stack>
                                    {step.action && <Box sx={{ width: { xs: "100%", sm: "auto" } }}>{step.action}</Box>}
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>

                        <Stack spacing={2}>
                          <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Número registrado</Typography>
                            <Stack spacing={1.25} mt={1.5}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <WhatsApp fontSize="small" color="success" />
                                <Typography variant="body2">{displayPhone || "Aún no hay número seleccionado"}</Typography>
                              </Stack>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Badge fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">WABA ID: {metaWhatsappStatus?.wabaId || lastWsApiSettings?.whatsappId || "Pendiente"}</Typography>
                              </Stack>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <CloudDone fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                  Modo: {isCoexistenceConnection ? "Coexistencia" : "Cloud API normal"}
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <FactCheck fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">Calidad: {META_QUALITY_LABELS[metaWhatsappStatus?.qualityRating || ""] || metaWhatsappStatus?.qualityRating || "Sin datos"}</Typography>
                              </Stack>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Send fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">Plantilla: {templateStatusLabel}</Typography>
                              </Stack>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Payment fontSize="small" color={missingPaymentMethod ? "warning" : "action"} />
                                <Typography variant="body2" color="text.secondary">
                                  Pago Meta: {missingPaymentMethod ? "Método de pago pendiente" : "Sin errores detectados"}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Box>
                          {!phoneRegistrationKnown && hasCloudCredentials && (
                            <Alert severity="warning" icon={<HelpOutline />}>
                              Falta registrar el número de teléfono con el PIN de 6 dígitos antes de enviar la plantilla de prueba.
                            </Alert>
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  )}
                  {isCloudProfileTab && (
                    <Box sx={{ mt: 2.5 }}>
                      {!whatsappConnected ? (
                        <Alert severity="info">
                          Conecta WhatsApp Cloud API antes de editar el perfil visible.
                        </Alert>
                      ) : (
                        <Box sx={{ p: { xs: 2, md: 2.5 }, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
                          <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 2 }}>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Perfil visible en WhatsApp</Typography>
                              <Typography variant="body2" color="text.secondary">Datos consultados desde Meta para el número conectado.</Typography>
                            </Box>
                            <Button variant="outlined" color="inherit" onClick={refreshBusinessProfile} disabled={loadingBusinessProfile} startIcon={loadingBusinessProfile ? <CircularProgress size={16} /> : <Refresh />}>
                              Consultar en Meta
                            </Button>
                          </Stack>
                          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "180px 1fr" }, gap: 2 }}>
                            <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, textAlign: "center", bgcolor: "background.default" }}>
                              <Box sx={{ width: 96, height: 96, mx: "auto", mb: 1.5, borderRadius: "50%", overflow: "hidden", display: "grid", placeItems: "center", bgcolor: "rgba(37, 211, 102, 0.12)", color: "success.dark" }}>
                                {businessProfile.profilePictureUrl ? <Box component="img" src={businessProfile.profilePictureUrl} alt="Foto de perfil de WhatsApp" sx={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <AccountCircle sx={{ fontSize: 72 }} />}
                              </Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Foto de perfil</Typography>
                              <Typography variant="caption" color="text.secondary">Sube el logo y LyPro lo registra en Meta.</Typography>
                            </Box>
                            <Stack spacing={1.5}>
                              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                                <Button
                                  variant="outlined"
                                  color="inherit"
                                  startIcon={uploadingProfileLogo || profileLogoStorage.loading ? <CircularProgress size={16} /> : <PhotoCamera />}
                                  disabled={uploadingProfileLogo || profileLogoStorage.loading || savingBusinessProfile}
                                  onClick={() => profileLogoInputRef.current?.click()}
                                >
                                  {uploadingProfileLogo || profileLogoStorage.loading ? "Subiendo logo" : "Subir logo"}
                                </Button>
                                <Typography variant="caption" color="text.secondary">
                                  Recomendado: imagen cuadrada JPG o PNG.
                                </Typography>
                                <input
                                  hidden
                                  ref={profileLogoInputRef}
                                  type="file"
                                  accept="image/png,image/jpeg,image/jpg"
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    event.target.value = "";
                                    if (file) void handleUploadProfileLogo(file);
                                  }}
                                />
                              </Box>
                              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                                <TextField label="Acerca de" value={businessProfile.about || ""} onChange={(event) => handleBusinessProfileChange("about", event.target.value)} fullWidth />
                                <TextField select label="Categoría" value={businessProfile.vertical || ""} onChange={(event) => handleBusinessProfileChange("vertical", event.target.value)} fullWidth>
                                  {WHATSAPP_VERTICAL_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                                </TextField>
                              </Stack>
                              <TextField label="Descripción" value={businessProfile.description || ""} onChange={(event) => handleBusinessProfileChange("description", event.target.value)} multiline minRows={3} fullWidth />
                              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                                <TextField label="Dirección" value={businessProfile.address || ""} onChange={(event) => handleBusinessProfileChange("address", event.target.value)} fullWidth />
                                <TextField label="Email" type="email" value={businessProfile.email || ""} onChange={(event) => handleBusinessProfileChange("email", event.target.value)} fullWidth />
                              </Stack>
                              <TextField label="Sitios web" value={profileWebsites} onChange={(event) => handleBusinessProfileChange("websites", event.target.value)} helperText="Un sitio por línea." multiline minRows={2} fullWidth />
                              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button variant="contained" onClick={handleSaveBusinessProfile} disabled={savingBusinessProfile || loadingBusinessProfile} startIcon={savingBusinessProfile ? <CircularProgress size={16} /> : <CheckCircle />}>
                                  {savingBusinessProfile ? "Guardando" : "Guardar perfil"}
                                </Button>
                              </Box>
                            </Stack>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                  {isCloudConfigTab && (
                <Box sx={{ mt: 2.5 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Credenciales de WhatsApp Cloud API
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Campos completados por el registro insertado de Meta.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        color="default"
                        label="Datos protegidos"
                        sx={{ fontWeight: 800 }}
                      />
                      {wsApiSettings?.maposBotWebhookUrl && (
                        <Chip
                          size="small"
                          color={wsApiSettings.maposBotWebhookSecretConfigured ? "success" : "warning"}
                          label={
                            wsApiSettings.maposBotWebhookSecretConfigured
                              ? "Proxy de MAPOS configurado"
                              : "Falta guardar el secreto del proxy"
                          }
                          sx={{ fontWeight: 800 }}
                        />
                      )}
                    </Stack>
                  </Stack>

                  {hasMaposNotificationsBot && (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        LYPRO-MAPOS-NOTIFICATIONS-BOT
                      </Typography>
                      <Typography variant="body2">
                        MAPOS envía las notificaciones a Lypro para que Lypro seleccione y envíe las plantillas aprobadas. Comparte con MAPOS este ID de cuenta emisora: <strong>{aiSettings?.companyId}</strong>. Lypro conserva el token de WhatsApp y su Phone Number ID. Endpoint: <strong>POST {getConfiguredApiBaseUrl()}/coexistence/notifications/send</strong>.
                      </Typography>
                    </Alert>
                  )}
                  {WS_SETTINGS_FIELDS.filter((field) =>
                    hasMaposNotificationsBot ||
                    !["maposBotWebhookUrl", "maposBotWebhookSecret"].includes(String(field.key)),
                  ).map((field) =>
                    renderSettingsField({
                      scope: "ws",
                      field,
                      value: wsApiSettings?.[field.key]
                        ? String(wsApiSettings[field.key])
                        : "",
                      originalValue: lastWsApiSettings?.[field.key]
                        ? String(lastWsApiSettings[field.key])
                        : "",
                      onChange: (nextValue) =>
                      wsSettingsFieldChange(field.key, nextValue),
                    }),
                  )}

                </Box>
                )}

                  {isCloudTemplatesTab && (
                <Box sx={{ mt: 2.5 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Plantillas de WhatsApp
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Crea plantillas propias, consulta su estado y envíalas manualmente.
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={refreshTemplateCatalog}
                        disabled={loadingTemplateCatalog || !whatsappConnected}
                        startIcon={
                          loadingTemplateCatalog ? <CircularProgress size={16} /> : <Refresh />
                        }
                      >
                        Actualizar
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => setCreateTemplateDialogOpen(true)}
                        disabled={!whatsappConnected}
                      >
                        Crear plantilla
                      </Button>
                    </Stack>
                  </Stack>

                  {hasMaposIntegration && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Avisos de pedidos MAPOS
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Lypro elige la plantilla según el estado recibido de MAPOS. Prepara cada borrador y envíalo a revisión de Meta desde el diálogo. Crear una plantilla no activa los avisos automáticos.
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {MAPOS_ORDER_TEMPLATE_DRAFTS.map((draft) => {
                          const existing = templateCatalog.find((template) => template.name === draft.name && template.language === "es");
                          return (
                            <Button key={draft.status} size="small" variant="outlined" disabled={Boolean(existing) || !whatsappConnected}
                              onClick={() => {
                                setTemplateDraft({ ...emptyTemplateDraft, name: draft.name, language: "es", category: "UTILITY", bodyText: draft.bodyText,
                                bodyExamples: ["12345"], buttons: [] });
                                setCreateTemplateDialogOpen(true);
                              }}>
                              {draft.label}{existing ? ` (${existing.status})` : " · Preparar"}
                            </Button>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}

                  {whatsappConnected && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Plantillas del servicio de coexistencia
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Lypro recibe el tipo de aviso, elige la plantilla aprobada y la envía por la línea conectada. Los borradores sirven para cualquier integración autorizada y el endpoint no conserva el contenido de los avisos.
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {COEXISTENCE_NOTIFICATION_TEMPLATE_DRAFTS.map((draft) => {
                          const existing = templateCatalog.find((template) => template.name === draft.name && template.language === "es");
                          return (
                            <Button key={draft.type} size="small" variant="outlined" disabled={Boolean(existing) || !whatsappConnected}
                              onClick={() => {
                                setTemplateDraft({ ...emptyTemplateDraft, name: draft.name, language: "es", category: "UTILITY", bodyText: draft.bodyText,
                                  bodyExamples: [...draft.bodyExamples], buttons: [] });
                                setCreateTemplateDialogOpen(true);
                              }}>
                              {draft.label}{existing ? ` (${existing.status})` : " · Preparar"}
                            </Button>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}

                  {!whatsappConnected ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Conecta WhatsApp Cloud API antes de administrar plantillas.
                    </Alert>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) repeat(2, minmax(150px, 1fr))" },
                          gap: 1,
                        }}
                      >
                        <TextField
                          size="small"
                          placeholder="Buscar por nombre o contenido"
                          value={templateSearch}
                          onChange={(event) => setTemplateSearch(event.target.value)}
                          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                        />
                        <TextField select size="small" label="Estado" value={templateStatusFilter} onChange={(event) => setTemplateStatusFilter(event.target.value)}>
                          <MenuItem value="ALL">Todos los estados</MenuItem>
                          {Object.entries(META_TEMPLATE_STATUS_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                        </TextField>
                        <TextField select size="small" label="Categoría" value={templateCategoryFilter} onChange={(event) => setTemplateCategoryFilter(event.target.value)}>
                          <MenuItem value="ALL">Todas las categorías</MenuItem>
                          {TEMPLATE_CATEGORY_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                        </TextField>
                      </Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.25 }}>
                        <Typography variant="caption" color="text.secondary">{filteredTemplateCatalog.length} de {templateCatalog.length} plantillas</Typography>
                        {(templateSearch || templateStatusFilter !== "ALL" || templateCategoryFilter !== "ALL") && (
                          <Button size="small" onClick={() => { setTemplateSearch(""); setTemplateStatusFilter("ALL"); setTemplateCategoryFilter("ALL"); }}>Limpiar filtros</Button>
                        )}
                      </Stack>
                    <Box
                      sx={{
                        mt: 2,
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: 1.5,
                      }}
                    >
                      {filteredTemplateCatalog.length ? (
                        filteredTemplateCatalog.map((template) => {
                          const statusLabel =
                            META_TEMPLATE_STATUS_LABELS[template.status || ""] ||
                            template.status ||
                            "Sin estado";
                          return (
                            <Box
                              key={`${template.name}-${template.language}`}
                              sx={{
                                p: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                bgcolor: "background.default",
                              }}
                            >
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1}
                                justifyContent="space-between"
                                alignItems={{ xs: "flex-start", sm: "center" }}
                              >
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                    {template.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {template.language} · {template.category || "Sin categoría"}
                                  </Typography>
                                </Box>
                                <Chip
                                  size="small"
                                  color={template.status === "APPROVED" ? "success" : "default"}
                                  label={statusLabel}
                                  sx={{ fontWeight: 800 }}
                                />
                              </Stack>
                              <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                                {template.components?.map((component, index) => (
                                  <Box key={`${template.name}-${component.type}-${index}`}>
                                    <Typography
                                      variant="caption"
                                      sx={{ display: "block", fontWeight: 800, color: "text.secondary" }}
                                    >
                                      {component.type}
                                    </Typography>
                                    {component.type === "BUTTONS" ? (
                                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                        {(component.buttons || []).map((button) => (
                                          <Chip
                                            key={`${template.name}-${button.text}`}
                                            size="small"
                                            variant="outlined"
                                            label={button.text}
                                          />
                                        ))}
                                      </Stack>
                                    ) : (
                                      <Typography variant="body2">
                                        {component.text || "Sin contenido"}
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                              </Stack>
                              <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1.25, bgcolor: "rgba(46, 125, 50, 0.06)", border: "1px solid rgba(46, 125, 50, 0.16)" }}>
                                <Typography variant="caption" sx={{ display: "block", fontWeight: 800, color: "text.secondary", mb: 0.75 }}>
                                  Vista previa en WhatsApp
                                </Typography>
                                <TemplatePreview
                                  headerText={getTemplateComponent(template, "HEADER")?.text}
                                  bodyText={getTemplateComponent(template, "BODY")?.text}
                                  footerText={getTemplateComponent(template, "FOOTER")?.text}
                                  buttons={getTemplateComponent(template, "BUTTONS")?.buttons?.map((button) => button.text)}
                                />
                              </Box>
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
                                <Button
                                  variant="outlined"
                                  color="inherit"
                                  fullWidth
                                  onClick={() => handleOpenTemplateSendDialog(template)}
                                >
                                  Enviar
                                </Button>
                              </Stack>
                            </Box>
                          );
                        })
                      ) : (
                        <Alert severity="info" sx={{ gridColumn: "1 / -1" }}>
                          Todavía no hay plantillas creadas para este WABA.
                        </Alert>
                      )}
                    </Box>
                </Box>
                  )}
                </Box>
                  )}

                  {isCloudConfigTab && hasWsSettingsChanges && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={openWsAPISettingsDialog}
                  >
                    Actualizar
                  </Button>
                )}
                </CardContent>
              </Card>
            </>
          )}

          {isProductsTab && (
            <Card sx={dashboardCardSx}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={dashboardSectionTitleSx}>
                  Catalogo
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  Importa categorias y productos desde la fuente configurada,
                  actualiza el catálogo y mejora la búsqueda.
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
                    Este proceso actualiza el catálogo de la compañía actual y
                    sincroniza la informacion disponible para consulta.
                  </Alert>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={openSyncDialog}
                    disabled={loadingSyncSettings}
                    startIcon={loadingSyncSettings ? undefined : <CloudDone />}
                  >
                    {loadingSyncSettings ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Actualizar catalogo"
                    )}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      <Dialog
        open={dialogAISettingsOpen}
        onClose={() => setDialogAISettingsOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar cambio</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            ¿Seguro que quieres realizar cambios de configuración?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAISettingsOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={confirmUpdateAiSettings}
            variant="contained"
            disabled={loadingAISettings}
          >
            {loadingAISettings ? <CircularProgress size={24} /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogWsApiSettingsOpen}
        onClose={() => setDialogWsApiSettingsOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar cambio</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            ¿Seguro que quieres realizar cambios de WhatsApp Business Platform?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogWsApiSettingsOpen(false)}>
            Cancelar
          </Button>

          <Button
            onClick={confirmUpdateWsAPISettings}
            variant="contained"
            disabled={loadingWsAPISettings}
          >
            {loadingWsAPISettings ? <CircularProgress size={24} /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={dialogSyncSettingsOpen}
        onClose={() => setDialogSyncSettingsOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar cambio</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Se consultara MAPOS para actualizar el catalogo operativo de esta
              compañía.
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Tambien se actualizaran categorias, productos e informacion de
              búsqueda.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogSyncSettingsOpen(false)}>
            Cancelar
          </Button>

          <Button
            onClick={confirmSyncSettings}
            variant="contained"
            disabled={loadingSyncSettings}
          >
            {loadingSyncSettings ? <CircularProgress size={24} /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={connectDialogOpen}
        onClose={() => setConnectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Conectar WhatsApp</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => handleConnectWhatsApp("coexistence")}
              disabled={connectingWhatsApp || resettingWhatsApp}
              startIcon={<WhatsApp color="success" />}
              sx={{
                justifyContent: "flex-start",
                p: 2,
                minHeight: 92,
                textAlign: "left",
                textTransform: "none",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Coexistencia
                  </Typography>
                  <Chip size="small" color="success" label="WhatsApp Business actual" />
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, textTransform: "none" }}
                >
                  Mantiene el mismo número activo en la app del teléfono y lo conecta a LyPro.
                </Typography>
              </Box>
            </Button>

            <Button
              variant="outlined"
              color="inherit"
              onClick={() => handleConnectWhatsApp("standard")}
              disabled={connectingWhatsApp || resettingWhatsApp}
              startIcon={<CloudDone color="primary" />}
              sx={{
                justifyContent: "flex-start",
                p: 2,
                minHeight: 92,
                textAlign: "left",
                textTransform: "none",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  Normal
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, textTransform: "none" }}
                >
                  Usa Cloud API tradicional para un número nuevo o una migración completa.
                </Typography>
              </Box>
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectDialogOpen(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={testTemplateDialogOpen}
        onClose={() => setTestTemplateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enviar plantilla de prueba</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Alert severity="info">
              Se enviará desde el número conectado: {displayPhone || "WhatsApp Cloud API"}.
              Estado de plantilla: {templateStatusLabel}.
            </Alert>
            <TextField
              label="Número destino"
              placeholder="573001112233"
              value={testPhoneNumber}
              onChange={(event) =>
                setTestPhoneNumber(event.target.value.replace(/[^\d]/g, ""))
              }
              inputProps={{ inputMode: "numeric" }}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                select
                label="Plantilla"
                value={testTemplateName}
                onChange={(event) => {
                  const nextName = event.target.value;
                  setTestTemplateName(nextName);
                  const nextTemplate = templateCatalog.find(
                    (template) => template.name === nextName,
                  );
                  setTestTemplateLanguage(nextTemplate?.language || "es");
                }}
                fullWidth
              >
                {templateCatalog.map((template) => (
                  <MenuItem key={`${template.name}-${template.language}`} value={template.name}>
                    {template.name} ({template.language})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Idioma"
                value={testTemplateLanguage}
                onChange={(event) => setTestTemplateLanguage(event.target.value)}
                autoComplete="off"
                fullWidth
              />
            </Stack>
            {selectedTemplate && (
              <Box
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.default",
                }}
              >
                <Typography variant="caption" sx={{ display: "block", fontWeight: 800, mb: 0.75 }}>
                  Vista previa
                </Typography>
                <Stack spacing={0.75}>
                  {selectedTemplate.components?.map((component, index) => (
                    <Box key={`${selectedTemplate.name}-${component.type}-${index}`}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                        {component.type}
                      </Typography>
                      {component.type === "BUTTONS" ? (
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {(component.buttons || []).map((button) => (
                            <Chip
                              key={`${selectedTemplate.name}-${button.text}`}
                              size="small"
                              variant="outlined"
                              label={button.text}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2">{component.text || "Sin contenido"}</Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestTemplateDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSendTestMessage}
            variant="contained"
            disabled={sendingTestMessage || !testPhoneNumber.trim() || !selectedTemplateApproved}
            startIcon={sendingTestMessage ? <CircularProgress size={16} /> : <Send />}
          >
            {sendingTestMessage ? "Enviando" : "Enviar plantilla"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createTemplateDialogOpen}
        onClose={() => setCreateTemplateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Crear plantilla de WhatsApp</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
            Diseña el mensaje, revisa la vista previa y envíalo a aprobación de Meta.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Alert severity="info">
              Meta revisa cada plantilla antes de permitir su envío. Usa un nombre descriptivo en minúsculas con guion bajo y escribe un mensaje claro, sin contenido engañoso ni spam.
            </Alert>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1 }}>
              {[
                ["1", "Identifica", "Nombre, idioma y categoría"],
                ["2", "Construye", "Encabezado, cuerpo, pie y botones"],
                ["3", "Envía a revisión", "Meta decide si la aprueba"],
              ].map(([step, title, description]) => (
                <Box key={step} sx={{ p: 1.1, borderRadius: 1.25, border: "1px solid", borderColor: "divider", bgcolor: "background.default" }}>
                  <Stack direction="row" spacing={0.9} alignItems="center">
                    <Box sx={{ width: 24, height: 24, display: "grid", placeItems: "center", borderRadius: "50%", bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 900, fontSize: 12 }}>{step}</Box>
                    <Typography variant="caption" sx={{ fontWeight: 900 }}>{title}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.55 }}>{description}</Typography>
                </Box>
              ))}
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Nombre"
                value={templateDraft.name}
                onChange={(event) =>
                  handleTemplateDraftChange(
                    "name",
                    event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
                  )
                }
                fullWidth
              />
              <TextField
                select
                label="Idioma"
                value={templateDraft.language}
                onChange={(event) => handleTemplateDraftChange("language", event.target.value)}
                helperText="Elige el idioma exacto del contenido."
                fullWidth
              >
                {TEMPLATE_LANGUAGE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              select
              label="Categoría"
              value={templateDraft.category || "UTILITY"}
              onChange={(event) => handleTemplateDraftChange("category", event.target.value)}
              fullWidth
            >
              {TEMPLATE_CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.25 }}>
              {TEMPLATE_CATEGORY_OPTIONS.find((option) => option.value === templateDraft.category)?.description}
            </Typography>
            <TextField
              label="Header"
              value={templateDraft.headerText || ""}
              onChange={(event) => handleTemplateDraftChange("headerText", event.target.value)}
              helperText="Opcional. Es el título corto que aparece arriba del mensaje."
              fullWidth
            />
            <TextField
              label="Body"
              value={templateDraft.bodyText}
              onChange={(event) => handleTemplateDraftChange("bodyText", event.target.value)}
              helperText="Obligatorio. Escribe aquí la información principal que recibirá el cliente."
              multiline
              minRows={4}
              fullWidth
            />
            {templateDraft.bodyText.includes("{{") && <TextField
              label="Ejemplos de las variables (uno por línea)"
              value={templateDraft.bodyExamples?.join("\n") || ""}
              onChange={(event) => setTemplateDraft((previous) => ({ ...previous, bodyExamples: event.target.value.split("\n") }))}
              helperText="Usa datos ficticios, en orden desde la variable 1. Se enviarán a Meta para revisar la plantilla."
              multiline minRows={2} fullWidth
            />}
            <TextField
              label="Footer"
              value={templateDraft.footerText || ""}
              onChange={(event) => handleTemplateDraftChange("footerText", event.target.value)}
              helperText="Opcional. Usa una nota breve, como horarios o condiciones."
              fullWidth
            />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.35 }}>Botones de respuesta rápida</Typography>
              <Typography variant="caption" color="text.secondary">Opcional. Agrega hasta 3 botones para que el cliente responda con un toque.</Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {[0, 1, 2].map((index) => (
                  <TextField
                    key={index}
                    size="small"
                    label={`Botón ${index + 1}`}
                    placeholder={index === 0 ? "Ej: Ver pedido" : "Opcional"}
                    value={templateDraft.buttons?.[index] || ""}
                    onChange={(event) => handleTemplateButtonChange(index, event.target.value)}
                    fullWidth
                  />
                ))}
              </Stack>
            </Box>
            <Box sx={{ p: 1.25, borderRadius: 1.5, border: "1px solid rgba(46, 125, 50, 0.2)", bgcolor: "rgba(46, 125, 50, 0.05)" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Vista previa en WhatsApp</Typography>
              <TemplatePreview
                headerText={templateDraft.headerText}
                bodyText={templateDraft.bodyText}
                footerText={templateDraft.footerText}
                buttons={templateDraft.buttons}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTemplateDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleCreateTemplate}
            variant="contained"
            disabled={creatingTemplate || !templateDraft.name.trim() || !templateDraft.bodyText.trim()}
          >
            {creatingTemplate ? <CircularProgress size={24} /> : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reiniciar conexión de WhatsApp</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Alert severity="warning">
              Esta acción borra en LyPro las credenciales guardadas y devuelve el
              flujo al primer paso.
            </Alert>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Usala cuando hayas removido LyPro AI como socio en Meta o cuando
              necesites repetir el registro insertado desde cero. Si WhatsApp ya
              funciona, no necesitas reiniciar la conexión.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleResetWhatsApp}
            variant="contained"
            color="error"
            disabled={resettingWhatsApp}
          >
            {resettingWhatsApp ? <CircularProgress size={24} /> : "Reiniciar"}
          </Button>
        </DialogActions>
      </Dialog>

      <FeedbackSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default SettingsPage;
